import React, {
  memo,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  type UIEvent
} from 'react';
import {
  Accordion,
  Button,
  ButtonVariant,
  Checkbox,
  AutoCompleteDropDown,
  ComboboxOption,
  DatePicker,
  FlyoutContainer,
  MRTDataTable,
  CmhcIcon,
  CmhcIconSize,
  MouseOrKeyboardEvent,
  ButtonSize,
  NotificationBanner,
  CmhcFlexIcon
} from '@cmhc-schl/hearth-ui';
import { Box, Stack, Chip, MenuItem, Menu, IconButton, Tooltip, Typography } from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useInventoryFilters } from 'Feature/V2/Shared_Portfolio/Hooks/useInventoryFilters';
import { useAuthenticationContext } from 'Foundation/Authentication';
import i18n from 'i18next';
import { MRT_Row, MRT_SortingState } from 'material-react-table';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Status,
  RequestType,
  SharepointDomain,
  PrefixFieldFolder,
  Success,
  UnSuccess,
  PortfolioStatus
} from '../../constants';
import { requestTypeMapping } from '../../utils/RequestTypeMapping';
import { statusMapping } from '../../utils/StatusMapping';
import {
  TableProps,
  LenderOptionType,
  FormErrors,
  ValidationReviewInventoryTableFields
} from '../HbtPortfolioManagementPage/types';
import {
  getInsuredApprovedLenderList,
  getValidationInventoryTableData,
  getApplicationErrorReport
} from '../HbtPortfolioManagementPage/ValidationInventoryService';
import styles from './styles.module.scss';
import { DropdownOptionType, FilterValuesType, FormErrorsType, TranslationType } from './types';
import { createPortfolioColumns } from './vriTableColumns';
import { buildSearchFilterApiQuery } from 'Feature/V2/Shared_Portfolio/utils/buildSearchFilterApiQuery';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { getLabel } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getLabel';
import { useMRTCustomTranslation } from 'Feature/V2/Shared_Portfolio/Hooks/useMRTCustomTransaltion';
import { removeDuplicateObjects } from 'Feature/V2/Shared_Portfolio/utils/removeDuplicateObjects';
import { ToastNotificationConfig } from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolDetails/types';
import { NotificationBannerType } from 'Feature/V2/HbtPIRequest/components/HbtPIRequestPage/types';

const normalizeExcelFileName = (fileName?: string) => {
  if (!fileName) {
    return '';
  }

  if (/\.(xls|xlsm|xlsx)$/i.test(fileName)) {
    return fileName.replace(/\.(xls|xlsm|xlsx)$/i, '.xlsx');
  }

  return fileName;
};

function getFilterDropdownValues<T extends Record<string, string> | string = string>(
  options: string[] | T[] | null,
  mapping?: Record<string, string> | undefined,
  labelKey: string = 'label',
  valueKey: string = 'value'
) {
  return (
    options?.map((option, index) => {
      if (typeof option === 'string') {
        return {
          id: index,
          label: mapping ? mapping[option] : option,
          value: option,
          isGroup: false,
          groupName: ''
        };
      }
      return {
        id: index,
        label: option[labelKey],
        value: option[valueKey],
        isGroup: false,
        groupName: ''
      };
    }) || []
  );
}

const fetchSize = 100;

const getRequestTypeLabel = (requestType: keyof typeof requestTypeMapping, t: TranslationType) => {
  return requestTypeMapping(t)[requestType];
};

const initialFilterValues = {
  requestType: undefined,
  status: undefined,
  ialn: undefined,
  startDate: undefined,
  endDate: undefined
};

const HbtPortfolioManagementTable: React.FC<TableProps> = ({
  openFlyout,
  toggleDrawer,
  updateFilters,
  toggleSearchDrawer,
  onDataStatusChange
}) => {
  const [tableError, setTableError] = useState<string>('');
  const authenticationContext = useAuthenticationContext();
  const [selectedRow, setSelectedRow] = useState<ValidationReviewInventoryTableFields>();
  const [formErrors, setFormErrors] = useState<FormErrors>();
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const history = useHistory();
  // sending as true for now till we finalize the solution for getting sucessfull and unsucessfull
  const isSuccess = true;
  const { t } = useTranslation('HbtPortfolioManagementPage');
  const env = encodeURIComponent(`${process.env.REACT_APP_ENV}`);
  const columns = useMemo(() => createPortfolioColumns(t, i18n.language), [t, i18n.language]);

  const [selectedFilters, setSelectedFilters] = useState({});
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const [fromDateInput, setFromDateInput] = useState<Date | null>(null);
  const [toDateInput, setToDateInput] = useState<Date | null>(null);

  const [showErrorBannerNotification, setShowErrorBannerNotification] = useState<boolean>(false);
  const [isDataTableErrorDismissed, setIsDataTableErrorDismissed] = useState<boolean>(false);

  const {
    activeFilters,
    changeFilter: handleFilterChange,
    clearFilter,
    filterValues,
    deleteChip,
    getChips: getActiveFilterChips,
    setActiveFilters,
    setFilterValues
  } = useInventoryFilters({ initialFilterValues, translation: 'HbtPortfolioManagementPage' });

  const stableFilters = useMemo(
    () => ({
      ...selectedFilters
    }),
    [selectedFilters]
  );

  const { dataType, searchQuery } = getSearchParams(location.search);

  const fetchInventoryData = async ({ pageParam = 0 }) => {
    const offset = pageParam;

    let sortField = sorting[0]?.id ?? '';
    if (sortField === 'insuredApprovedLenderName') {
      sortField = i18n.language === 'en' ? 'approvedLenderEnglishName' : 'approvedLenderFrenchName';
    }
    const sortOrder = !!sorting[0]?.desc;

    const sizeQueryParams = `?skip=${offset}&take=${fetchSize}`;
    const filterQueryParams = buildSearchFilterApiQuery(selectedFilters);

    const { searchQuery } = getSearchParams(location.search);
    const searchQueryParams = searchQuery ? buildSearchFilterApiQuery(searchQuery) : '';

    const sortFieldQueryParams = `${
      sortField ? `&orderBy=${sortField} ${sortOrder ? 'desc' : 'asc'}` : ''
    }`;

    const queryParams = `${sizeQueryParams}${filterQueryParams}${searchQueryParams}${sortFieldQueryParams}`;

    const data = await getValidationInventoryTableData({
      authenticationContext,
      queryParams
    });
    return data;
  };

  const {
    data,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error: dataTableError
  } = useInfiniteQuery({
    queryKey: ['portfolio-validation-inventory-table', stableFilters, sorting, location.search],
    queryFn: fetchInventoryData,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.flat().length;
      const hasMore = lastPage.length === fetchSize;
      return hasMore ? totalFetched : undefined;
    }
  });

  const showDataTableErrorBanner = !!dataTableError && !isDataTableErrorDismissed;

  const dataTableErrorBannerConfig = useMemo<Pick<ToastNotificationConfig, 'header' | 'subHeader'>>(
    () => ({
      header: t('PI-Portfolio-API-Error-Header'),
      subHeader: t('PI-Portfolio-Table-Notification-Error-Body')
    }),
    [t]
  );

  const flatData = useMemo(() => data?.pages.flat() ?? [], [data]);
  const mrtCustomTranslation = useMRTCustomTranslation(flatData.length);

  useEffect(() => {
    if (onDataStatusChange) {
      if (flatData.length === 0) {
        onDataStatusChange(false);
      } else {
        onDataStatusChange(true);
      }
    }
  }, [flatData, onDataStatusChange]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 400 && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage]
  );

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const handleClick = (evt: React.MouseEvent<HTMLElement>) => {
    if (evt) {
      evt.stopPropagation();
    }
    setAnchorElement(evt.currentTarget);
  };

  const handleClose = (evt: React.MouseEvent<HTMLElement>) => {
    if (evt) {
      evt.stopPropagation();
    }
    setAnchorElement(null);
  };

  const handleClearFilter = () => {
    clearFilter();
    setFormErrors({});
    setSelectedFilters({});
    setFromDateInput(null);
    setToDateInput(null);
  };

  const handleFilterSubmit = async (
    activeFilters?: FilterValuesType,
    nextFilterValues?: FilterValuesType
  ) => {
    const currentFilterValue = activeFilters || nextFilterValues || filterValues;
    try {
      let fromDate =
        (fromDateInput && moment(fromDateInput).format('YYYY-MM-DD')) ??
        currentFilterValue.startDate;
      let toDate =
        (toDateInput && moment(toDateInput).format('YYYY-MM-DD')) ?? currentFilterValue.endDate;
      const fromDateMoment = moment(fromDate);
      const toDateMoment = moment(toDate);
      fromDate = fromDateMoment.isValid() ? fromDate : undefined;
      toDate = toDateMoment.isValid() ? toDate : undefined;

      const today = moment().endOf('day');

      const errors: FormErrorsType = {};
      if (fromDate && !toDate) {
        errors.toDate = true;
      }
      if ((!fromDate && toDate) || fromDateMoment.isAfter(today)) {
        errors.fromDate = true;
      }

      if (fromDate && toDate && toDateMoment.isBefore(fromDateMoment)) {
        errors.toDate = true;
        errors.dateRange = t('Pi-Portfolio-Management-Table-Filter-Date-DateRange-Error-Message');
      }

      if (
        !currentFilterValue.ialn &&
        !currentFilterValue.requestType &&
        !currentFilterValue.status &&
        !fromDate &&
        !toDate
      ) {
        toggleDrawer(false);
        return;
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
      setFormErrors({});
      if (!activeFilters) {
        setActiveFilters({
          ...currentFilterValue,
          startDate: fromDate ?? undefined,
          endDate: toDate ?? undefined
        });
      }
      const filter = {
        requestType: currentFilterValue.requestType ?? [],
        status: currentFilterValue.status?.map((item: DropdownOptionType) => item.value) ?? [],
        insuredApprovedLender:
          currentFilterValue.ialn?.map((item: DropdownOptionType) => item.value) ?? [],
        requestReceiptDateFrom: fromDate,
        requestReceiptDateTo: toDate
      };
      setSelectedFilters(filter);
      setFilterValues({
        ...currentFilterValue,
        startDate: fromDate ?? undefined,
        endDate: toDate ?? undefined
      });
      toggleDrawer(false);
    } catch (error) {
      setTableError('Error fetching filtered data.');
    }
  };

  const handleChipDelete = (chip: { key: string }) => {
    const { nextActiveFilters, nextFiltersValues } = deleteChip(chip);

    getActiveFilterChips().length === 1
      ? handleClearFilter()
      : handleFilterSubmit(nextActiveFilters, nextFiltersValues);
  };

  const onRowSelect = (row: any) => {
    const rowData = row.original;
    const route = 'pages/portfolio-management/request-overview';
    const searchExist = !!location.search;
    const sanitizedPortfolioIds = (rowData?.portfolioIds ?? [])
      .map((portfolio: string) => portfolio.split(':')[0]?.trim())
      .filter(Boolean)
      .join();
    history.push(
      `/${i18n.language}/${route}${searchExist ? location.search + '&' : '?'}appID=${
        rowData?.applicationId
      }&pfID=${sanitizedPortfolioIds}&pptCode=${rowData?.portfolioPoolPrecisionTypeCode}`
    );
  };

  const RowActions = ({ row }: { row: MRT_Row<ValidationReviewInventoryTableFields> }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpenMenu = (event: any) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
      setAnchorEl(null);
    };

    const handleGetErrorReport = async (
      applicationId: number,
      row: ValidationReviewInventoryTableFields
    ) => {
      const errorReport = await getApplicationErrorReport(authenticationContext, applicationId);
      if (!errorReport) return;

      const dispo = errorReport.headers['content-disposition'] ?? '';
      const match = dispo.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] ?? 'data.xlsx';

      const url = URL.createObjectURL(errorReport.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    };

    const handleErrorReport = async () => {
      await handleGetErrorReport(row.original.applicationId, row.original);
      handleCloseMenu();
    };

    const handleFileDownload = () => {
      const fileId = row?.original.pirfFile?.fileId;
      const normalizedFileId = normalizeExcelFileName(fileId);
      const url = `${SharepointDomain}/${env}/${
        isSuccess ? Success : UnSuccess
      }/${normalizedFileId}`;
      window.open(url, '_blank');
      handleCloseMenu();
    };

    return (
      <Box>
        <Tooltip title="Actions">
          <IconButton onClick={handleOpenMenu}>
            <CmhcIcon size={CmhcIconSize.SMALL} type="icon_more_vertical" />
          </IconButton>
        </Tooltip>
        <Menu
          className={styles.dataTableContainerPopupMenuContainer}
          sx={{
            minWidth: '250px',
            '.MuiMenuItem-root': {
              minHeight: '50px'
            },
            '.MuiPaper-root': {
              boxShadow: 'unset',
              border: '2px solid #697885',
              width: 'fit-content'
            }
          }}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          onClick={(e) => e.stopPropagation()}
        >
          {/* <MenuItem className={styles.popupMenu} data-testid="download-menu-item">
            <Typography>{t('AvailableFiles')}</Typography>
          </MenuItem> */}
          <Box className={styles.onRowActionItemsHeader}>{t('AvailableFiles')}</Box>
          <Box className={styles.onRowActionBody}>
            <MenuItem
              className={styles.popupMenu}
              onClick={handleFileDownload}
              data-testid="download-pirf-file-menu-item"
            >
              <CmhcFlexIcon width={16} height={16} type="icon_dataset" />
              <span style={{ whiteSpace: 'nowrap' }}>
                {row?.original.pirfFile?.name ? `PIRF: ${row?.original.pirfFile?.name}` : ''}
              </span>
            </MenuItem>

            <MenuItem
              className={styles.popupMenu}
              onClick={handleErrorReport}
              data-testid="view-error-report-menu-item"
            >
              <CmhcFlexIcon width={16} height={16} type="icon_dataset" />
              <span>{t('View-Error-Report')}</span>
            </MenuItem>
          </Box>
        </Menu>
      </Box>
    );
  };

  const { data: filterDropdownData } = useQuery({
    queryKey: ['portfolio-management-approved-lender'],
    queryFn: async () => getInsuredApprovedLenderList(authenticationContext),
    initialData: [] as LenderOptionType[] | null
  });

  useEffect(() => {
    updateFilters(getActiveFilterChips());
  }, [activeFilters]);

  const lenderList = useMemo(() => {
    const flatLenderList = filterDropdownData && removeDuplicateObjects(filterDropdownData);
    return getFilterDropdownValues<LenderOptionType>(
      flatLenderList,
      undefined,
      i18n.language === 'en' ? 'approvedLenderEnglishName' : 'approvedLenderFrenchName',
      'lenderCode'
    );
  }, [filterDropdownData]);

  useEffect(() => {
    if (!filterValues?.endDate) {
      setToDateInput(null);
    }
    if (!filterValues?.startDate) {
      setFromDateInput(null);
    }
  }, [filterValues?.endDate, filterValues?.startDate]);

  const handleSearchAgain = () => {
    toggleSearchDrawer && toggleSearchDrawer(true, true);
  };

  const handleRequestTypeFilterChange = (
    filterType: string,
    selectedValue: DropdownOptionType | ComboboxOption | boolean | Date | string,
    item = ''
  ) => {
    if (filterType === 'requestType') {
      const values = filterValues?.requestType || [];
      const requestType = new Set(values as string[]);
      if (selectedValue) {
        requestType.add(item as string);
      } else {
        requestType.delete(item);
      }
      setFilterValues((prevState) => ({
        ...prevState,
        [filterType]: [...requestType]
      }));
    }
  };

  const showNotificationBanner = showErrorBannerNotification || showDataTableErrorBanner;

  const handleNotificationBannerClose = () => {
    if (showDataTableErrorBanner) {
      setIsDataTableErrorDismissed(true);
      return;
    }

    setShowErrorBannerNotification(false);
  };

  return (
    <div>
      <div>
        <FlyoutContainer
          id="portfolio-filter-flyout-container"
          isHeaderRequired={true}
          headerText={t('Filter')}
          open={openFlyout}
          onClose={() => {
            toggleDrawer(false);
            setFormErrors({});
            setFilterValues({
              ...activeFilters,
              startDate: activeFilters.startDate
                ? moment(activeFilters.startDate).toDate()
                : undefined,
              endDate: activeFilters.endDate ? moment(activeFilters.endDate).toDate() : undefined
            });
          }}
          content={
            <Box
              className={styles.flyoutContainer}
              role="presentation"
              sx={{
                pt: 3
              }}
            >
              <Box>
                <Accordion
                  accordionData={{
                    heading: `${t('Pi-Portfolio-Management-Table-Column-RequestType')} ${filterValues.requestType?.length > 0 ? `(${filterValues.requestType?.length})` : ''}`,
                    defaultExpanded: true,
                    contentSlot: (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        className={styles.requestTypeCheckboxContainer}
                      >
                        {RequestType.map((item, index) => {
                          return (
                            <Checkbox
                              key={index}
                              showCheckboxGroup={false}
                              checked={!!filterValues.requestType?.includes(item)}
                              onChange={(evt) =>
                                handleRequestTypeFilterChange(
                                  'requestType',
                                  evt.target.checked,
                                  item
                                )
                              }
                              label={{
                                value: getRequestTypeLabel(
                                  item as keyof typeof requestTypeMapping,
                                  t
                                )
                              }}
                            />
                          );
                        })}
                      </Box>
                    )
                  }}
                ></Accordion>
                <Accordion
                  accordionData={{
                    heading: t('Pi-Portfolio-Management-Table-Column-RequestReceiptDate'),
                    defaultExpanded: true,
                    contentSlot: (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '24px'
                        }}
                      >
                        <DatePicker
                          value={
                            fromDateInput !== null && moment(fromDateInput).isValid()
                              ? fromDateInput
                              : filterValues?.startDate
                                ? new Date(filterValues.startDate)
                                : null
                          }
                          placeholderText={t('DateFormat')}
                          onChange={(value) => {
                            const cleared = value === null;

                            if (cleared) {
                              setFromDateInput(null);
                              setFilterValues((prev) => ({ ...prev, startDate: undefined }));
                              setActiveFilters((prev) => ({ ...prev, startDate: undefined }));
                            } else {
                              setFromDateInput(value);
                            }

                            if (!value && !toDateInput) {
                              setFormErrors({
                                ...formErrors,
                                fromDate: undefined,
                                toDate: undefined,
                                dateRange: undefined
                              });
                            }
                          }}
                          format="yyyy-MM-dd"
                          error={formErrors?.fromDate}
                          errorMessage={t(
                            'Pi-Portfolio-Management-Table-Filter-Date-Error-Message'
                          )}
                          label={t('Pi-Portfolio-Management-Table-Filter-Date-Start-Date')}
                          disableFuture
                        />
                        <DatePicker
                          value={
                            toDateInput !== null && moment(toDateInput).isValid()
                              ? toDateInput
                              : filterValues?.endDate
                                ? new Date(filterValues.endDate)
                                : null
                          }
                          placeholderText={t('DateFormat')}
                          onChange={(value) => {
                            const cleared = value === null;

                            if (cleared) {
                              setToDateInput(null);
                              setFilterValues((prev) => ({ ...prev, endDate: undefined }));
                              setActiveFilters((prev) => ({ ...prev, endDate: undefined }));
                            } else {
                              setToDateInput(value);
                            }

                            if (!value && !fromDateInput) {
                              setFormErrors({
                                ...formErrors,
                                toDate: undefined,
                                fromDate: undefined,
                                dateRange: undefined
                              });
                            }
                          }}
                          error={formErrors?.toDate || !!formErrors?.dateRange}
                          errorMessage={
                            formErrors?.dateRange ??
                            t('Pi-Portfolio-Management-Table-Filter-Date-Error-Message')
                          }
                          format="yyyy-MM-dd"
                          label={t('Pi-Portfolio-Management-Table-Filter-Date-End-Date')}
                          disableFuture
                        />
                      </Box>
                    )
                  }}
                ></Accordion>
                <Accordion
                  accordionData={{
                    heading: `${t('Pi-Portfolio-Management-Table-Column-Status')} ${filterValues.status?.length > 0 ? `(${filterValues.status?.length})` : ''}`,
                    defaultExpanded: true,
                    contentSlot: (
                      <Box>
                        <AutoCompleteDropDown
                          ariaText={{
                            value: 'test'
                          }}
                          value={filterValues.status?.map((item: DropdownOptionType) => item.value)}
                          onChange={(evt: MouseOrKeyboardEvent, option: ComboboxOption) =>
                            handleFilterChange('status', option)
                          }
                          options={getFilterDropdownValues(Status, statusMapping(t))}
                          placeholderText={t('Select')}
                        />
                      </Box>
                    )
                  }}
                ></Accordion>

                <Accordion
                  suppressBottomBorder={true}
                  accordionData={{
                    heading: `${t('Pi-Portfolio-Management-Table-Column-InsuredApprovedLenderName')} ${filterValues.ialn?.length > 0 ? `(${filterValues.ialn?.length})` : ''}`,
                    defaultExpanded: true,
                    contentSlot: (
                      <Box>
                        <AutoCompleteDropDown
                          ariaText={{
                            value: 'test'
                          }}
                          value={filterValues.ialn?.map((item: DropdownOptionType) => item.value)}
                          onChange={(evt: MouseOrKeyboardEvent, option: ComboboxOption) =>
                            handleFilterChange('ialn', option)
                          }
                          options={lenderList}
                          placeholderText={t('Select')}
                        />
                      </Box>
                    )
                  }}
                ></Accordion>
              </Box>
              <Box className={styles.flyoutFooter}>
                <Stack direction="row" spacing={3}>
                  <Button
                    type="button"
                    name={`btn`}
                    onClick={handleClearFilter}
                    className={styles.fullWidth}
                    variant={ButtonVariant.SECONDARY}
                    text={{
                      value: t('Pi-Portfolio-Management-Table-Filter-Date-Clear-Filters')
                    }}
                    ariaText={{
                      value: ''
                    }}
                  />

                  <Button
                    type="submit"
                    name={`btn`}
                    onClick={() => handleFilterSubmit()}
                    className={styles.fullWidth}
                    variant={ButtonVariant.PRIMARY}
                    text={{
                      value: t('Pi-Portfolio-Management-Table-Filter-Date-Show-Results')
                    }}
                    ariaText={{
                      value: ''
                    }}
                  />
                </Stack>
              </Box>
            </Box>
          }
        />
      </div>
      {showNotificationBanner && (
        <div className={styles.notificationBanner}>
          <NotificationBanner
            key={dataTableErrorBannerConfig.header}
            showNotificationBanner={showNotificationBanner}
            onCloseNotificationBanner={handleNotificationBannerClose}
            headerText={{ value: dataTableErrorBannerConfig.header }}
            message={{ value: dataTableErrorBannerConfig.subHeader }}
            variant={NotificationBannerType.ERROR}
          />
        </div>
      )}
      {!!Object.values(searchQuery)[0] && !isLoading && dataType && (
        <Box className={styles.searchResultText}>
          {flatData.length === 0
            ? t('Search-Result-No-Data-subheader1')
            : flatData.length > 0
              ? t('Search-Result-No-Data-content1')
              : ''}
          {
            <>
              {t(`${getLabel(dataType)}`)}{' '}
              <data className={styles.queryContainer}>{Object.values(searchQuery ?? {})[0]}.</data>{' '}
              {t('Search-Result-No-Data-content2')}
              <Button
                name={t('Search-Result-No-Data-content3')}
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                onClick={handleSearchAgain}
                text={{ value: t('Search-Result-No-Data-content3') }}
                ariaText={{ value: t('Search-Result-No-Data-content3') }}
              />
              {t('Search-Result-No-Data-content4')}
              {t('or')}
              <Button
                name={t('clear')}
                size={ButtonSize.SMALL}
                variant={ButtonVariant.TERTIARY}
                onClick={() => {
                  history.replace({
                    pathname: location.pathname
                  });
                }}
                text={t('clear').toLocaleLowerCase()}
                ariaText={t('clear')}
              />
              {t('search-results')}
            </>
          }
        </Box>
      )}
      {getActiveFilterChips().length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 2 }} className={styles.chipContainer}>
          {getActiveFilterChips().map((chip) => (
            <Chip
              key={chip.key}
              label={chip.label}
              className={styles.chipStyles}
              onDelete={() => handleChipDelete(chip)}
            />
          ))}
          <Button
            onClick={() => {
              handleClearFilter();
              setActiveFilters(initialFilterValues);
            }}
            variant={ButtonVariant.TERTIARY}
            name="Clear Filters"
            text={{
              value: t('Pi-Portfolio-Management-Table-Filter-Date-Clear-Filters')
            }}
            leadingIcon={() => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_clear_circle" />}
            className={styles.clearAllFiltersButton}
          >
            {t('Pi-Portfolio-Management-Table-Filter-Date-Clear-Filters')}
          </Button>
        </Box>
      )}
      {!Object.values(searchQuery)[0] &&
        !isLoading &&
        flatData.length === 0 &&
        getActiveFilterChips().length > 0 && (
          <Box className={styles.noDataText}>{t('Pi-Portfolio-Management-No-Data-Text')}</Box>
        )}
      <div className={styles.dataTableContainer}>
        <MRTDataTable<ValidationReviewInventoryTableFields>
          data={flatData}
          columns={columns}
          localization={mrtCustomTranslation}
          onRowClick={onRowSelect}
          enableRowVirtualization={true}
          enableColumnVirtualization={true}
          pageSize={100}
          memoMode="cells"
          columnResizeMode="onChange"
          enableRowActions
          enableSorting={true}
          muiTableBodyRowProps={({ row }) => ({
            tabIndex: 0,
            onClick: () => onRowSelect(row),
            onKeyDown: (e) => {
              if (e.key === 'Enter') {
                onRowSelect(row);
              }
            }
          })}
          initialState={{
            columnPinning: { left: ['mrt-row-actions', 'applicationId'] }
          }}
          enableBottomToolbar={false}
          enablePagination={false}
          enableStickyHeader={true}
          enableSortingRemoval={false}
          renderRowActions={({ row }) => <RowActions row={row} />}
          muiTableContainerProps={{
            ref: tableContainerRef,
            sx: { maxHeight: '600px', minHeight: '300px' },
            onScroll: (event: UIEvent<HTMLDivElement>) =>
              fetchMoreOnBottomReached(event.target as HTMLDivElement)
          }}
          manualSorting
          onSortingChange={setSorting}
          state={{
            isLoading,
            sorting
          }}
          renderEmptyRowsFallback={() => <></>}
        />
      </div>
    </div>
  );
};

export default memo(HbtPortfolioManagementTable);
